/**
 * DiscardPileSprite - 捨て札スプライト
 * トップカードの表示と宣言色インジケーターを担当
 */
import Phaser from 'phaser';
import { Card } from '../../core/Card';
import { CardColor, CardType } from '../../types/card';
import { PHASER_CONFIG } from '../../utils/constants';

export class DiscardPileSprite extends Phaser.GameObjects.Container {
  private cardImage!: Phaser.GameObjects.Image;
  private colorIndicator!: Phaser.GameObjects.Graphics;
  private colorIndicatorBg!: Phaser.GameObjects.Graphics;
  private currentCard: Card | null = null;
  private declaredColor: CardColor | null = null;

  // 色のHEX値マッピング
  private static readonly COLOR_MAP: Record<CardColor, number> = {
    [CardColor.RED]: 0xe74c3c,
    [CardColor.BLUE]: 0x3498db,
    [CardColor.YELLOW]: 0xf39c12,
    [CardColor.WILD]: 0x9b59b6,
  };

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);

    this.createCardDisplay();
    this.createColorIndicator();
  }

  /**
   * カード表示を作成
   */
  private createCardDisplay(): void {
    // 薄い影のような背景
    const shadow = this.scene.add.graphics();
    shadow.fillStyle(0x000000, 0.3);
    shadow.fillRoundedRect(
      -PHASER_CONFIG.CARD_SIZE.width / 2 + 5,
      -PHASER_CONFIG.CARD_SIZE.height / 2 + 5,
      PHASER_CONFIG.CARD_SIZE.width,
      PHASER_CONFIG.CARD_SIZE.height,
      12
    );
    this.add(shadow);

    // カード画像（初期は非表示）
    this.cardImage = this.scene.add.image(0, 0, 'card_back');
    this.cardImage.setDisplaySize(
      PHASER_CONFIG.CARD_SIZE.width,
      PHASER_CONFIG.CARD_SIZE.height
    );
    this.cardImage.setVisible(false);
    this.add(this.cardImage);
  }

  /**
   * 色インジケーターを作成
   */
  private createColorIndicator(): void {
    // 背景
    this.colorIndicatorBg = this.scene.add.graphics();
    this.colorIndicatorBg.fillStyle(0x000000, 0.8);
    this.colorIndicatorBg.fillCircle(
      PHASER_CONFIG.CARD_SIZE.width / 2 + 10,
      -PHASER_CONFIG.CARD_SIZE.height / 2 + 10,
      25
    );
    this.colorIndicatorBg.setVisible(false);
    this.add(this.colorIndicatorBg);

    // 色の円
    this.colorIndicator = this.scene.add.graphics();
    this.colorIndicator.setVisible(false);
    this.add(this.colorIndicator);
  }

  /**
   * トップカードを設定
   */
  public setTopCard(card: Card): void {
    this.currentCard = card;

    const textureKey = this.getTextureKey(card);
    this.cardImage.setTexture(textureKey);
    this.cardImage.setDisplaySize(
      PHASER_CONFIG.CARD_SIZE.width,
      PHASER_CONFIG.CARD_SIZE.height
    );
    this.cardImage.setVisible(true);

    // ワイルドカードでなければ色インジケーターを非表示
    if (
      card.type !== CardType.WILD &&
      card.type !== CardType.WILD_DRAW_FOUR
    ) {
      this.colorIndicator.setVisible(false);
      this.colorIndicatorBg.setVisible(false);
      this.declaredColor = null;
    }
  }

  /**
   * 宣言色を表示
   */
  public showDeclaredColor(color: CardColor): void {
    if (color === CardColor.WILD) return;

    this.declaredColor = color;

    // 色インジケーターを更新
    this.colorIndicator.clear();
    this.colorIndicator.fillStyle(DiscardPileSprite.COLOR_MAP[color], 1);
    this.colorIndicator.fillCircle(
      PHASER_CONFIG.CARD_SIZE.width / 2 + 10,
      -PHASER_CONFIG.CARD_SIZE.height / 2 + 10,
      20
    );

    this.colorIndicatorBg.setVisible(true);
    this.colorIndicator.setVisible(true);

    // 脈動アニメーション
    this.scene.tweens.add({
      targets: [this.colorIndicator, this.colorIndicatorBg],
      scaleX: { from: 1, to: 1.2 },
      scaleY: { from: 1, to: 1.2 },
      duration: 300,
      yoyo: true,
      repeat: 2,
      ease: 'Sine.easeInOut',
    });
  }

  /**
   * カードのテクスチャキーを取得
   */
  private getTextureKey(card: Card): string {
    const type = card.type;
    const value = card.value;

    // ワイルドカード
    if (type === CardType.WILD) {
      return 'card_wild';
    }
    if (type === CardType.WILD_DRAW_FOUR) {
      return 'card_wild_draw4';
    }

    // 色名を取得
    const colorName = this.getColorName(card.color);

    // 数字カード
    if (type === CardType.NUMBER && value !== undefined) {
      return `card_${colorName}_${value}`;
    }

    // アクションカード
    switch (type) {
      case CardType.SKIP:
        return `card_${colorName}_skip`;
      case CardType.REVERSE:
        return `card_${colorName}_reverse`;
      case CardType.DRAW_TWO:
        return `card_${colorName}_draw2`;
      default:
        return 'card_back';
    }
  }

  /**
   * 色名を取得
   */
  private getColorName(color: CardColor): string {
    switch (color) {
      case CardColor.RED:
        return 'red';
      case CardColor.BLUE:
        return 'blue';
      case CardColor.YELLOW:
        return 'yellow';
      default:
        return 'red';
    }
  }

  /**
   * カード到着アニメーション
   */
  public animateCardArrival(card: Card): void {
    // 新しいカードを大きく表示してから通常サイズに
    this.setTopCard(card);

    this.cardImage.setScale(1.3);
    this.scene.tweens.add({
      targets: this.cardImage,
      scaleX: 1,
      scaleY: 1,
      duration: 200,
      ease: 'Back.easeOut',
    });
  }

  /**
   * 現在のカードを取得
   */
  public getCurrentCard(): Card | null {
    return this.currentCard;
  }

  /**
   * 宣言色を取得
   */
  public getDeclaredColor(): CardColor | null {
    return this.declaredColor;
  }

  /**
   * カード表示をクリア
   */
  public clear(): void {
    this.currentCard = null;
    this.declaredColor = null;
    this.cardImage.setVisible(false);
    this.colorIndicator.setVisible(false);
    this.colorIndicatorBg.setVisible(false);
  }
}
